import type { SignatureType } from './signing';

export enum ViewportSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export enum ViewType {
  LIST = 0,
  TILE = 1,
}

export type CalendarView = 'day' | 'week' | 'month' | 'agenda';

export type ProcessType = 'signDocs' | 'signRequest' | 'signSeal';

export type FileProcessType = Exclude<ProcessType, 'signSeal'>;

export const toFileProcessType = (
  value: Nullable<string> | undefined
): FileProcessType | undefined =>
  value === 'signDocs' || value === 'signRequest' ? value : undefined;

export interface FunctionEnabled {
  type: string;
  enabled: boolean;
}

export interface Price {
  currency: string;
  price: number;
}

export interface Country {
  name: string;
  code: string;
  phoneCode: string;
  default?: true;
}

export interface SystemNotificationList {
  notificationId: number;
  title: string;
  text: string;
  length: number;
}

export interface Item {
  shortName: string;
  rid: number;
  displayName: string;
  subtype: number;
  type: ResourceType;
  status: number;
  permission: number;
  imageDefined: boolean;
  members: Item[];
}

export interface Duration {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface Comment {
  text: string;
  commentId: number;
  replyTo: number;
  authorId: number;
  createDate: number;
  replies: Comment[];
  authorName: string;
  authorShortName: string;
  authorImage: string;
  deleted: boolean;
  updateDate: number;
  authorImageDefined: boolean;
  authorDeleted: boolean;
  authorSubtype: number;
}

export interface Checksum {
  algorithm: string;
  value: string;
}

export enum ExpirationDuration {
  CUSTOM = 0,
  DAY = 1,
  WEEK = 2,
  TWO_WEEKS = 3,
  MONTH = 4,
  UNLIMITED = 5,
}

export enum Action {
  LOGIN = 1,
  LOGOUT = 2,
  SESSION_TIMEOUT = 3,
  INSERT = 4,
  UPDATE = 5,
  DELETE = 6,
  DOWNLOAD = 7,
  SEC_VIEW = 8,
  SEC_PRINT = 9,
  MOVE = 10,
  ZIP = 11,
  UNZIP = 12,
  EXPORT_DATA = 14,
  CREATE_GUEST = 15,
  DELETE_MEMBERSHIP = 16,
  INSERT_MEMBERSHIP = 17,
  INSERT_VERSION = 23,
  DELETE_VERSION = 24,
  RESTORE_VERSION = 25,
  DOWNLOAD_VERSION = 26,
  SEND_LOGIN_INFO = 27,
  EVENT_INVITATION_UPDATE = 31,
  CHANGE_PASSWORD = 35,
  ENABLE_USER = 38,
  DISABLE_USER = 39,
  LOGIN_FAILED_PASSWORD = 43,
  APPROVAL_APPROVED = 47,
  APPROVAL_REJECTED = 48,
  APPROVAL_ABSTAINED = 49,
  COMMUNITY_WIPE = 52,
  COMMUNITY_EMERGENCY_CODE_GENERATE = 64,
  COMMUNITY_EMERGENCY_CODE_LOGIN = 65,
  ZIP_ENTRY = 66,
  UPDATE_SYSTEM = 67,
  INSERT_COMMENT = 68,
  DELETE_COMMENT = 69,
  PERMISSIONS = 70,
  MODIFY_NOTIFICATION_SELECTION = 71,
  MODIFY_APPROVALS = 72,
  MODIFIED_NOTIFICATION_SETTINGS = 73,
  MODIFY_INVITEES = 74,
  LDAP_SYNCHRONIZATION = 75,
  INSERT_BOOKMARK = 76,
  DELETE_BOOKMARK = 77,
  SIGN_DOCUMENT = 78,
  EXPORT = 79,
  LOGIN_CHALLENGE = 80,
  LOGIN_MOBILE_ID = 81,
  LOGIN_CERTIFICATE = 82,
  LOGIN_FAILED_CHALLENGE = 83,
  LOGIN_FAILED_CHALLENGE_EXPIRED = 84,
  LOGIN_FAILED_MOBILE_ID = 85,
  LOGIN_FAILED_CERTIFICATE = 86,
  LOGIN_FAILED_USER_DISABLED = 87,
  LOGIN_FAILED_USER_LOCKED = 88,
  SEND_NEW_PASSWORD = 89,
  SEND_LOGIN_INFO_WITHOUT_PASSWORD = 90,
  LOGIN_OTP = 91,
  MODIFY_SIGNATURES = 92,
  VOTING_START = 93,
  VOTING_STOP = 94,
  VOTING_RESTART = 95,
  MODIFY_VOTING_OPTIONS = 96,
  OTP_SHARED_KEY_RESET = 97,
  OTP_SHARED_KEY_GENERATION = 98,
  MODIFY_ATTENDERS = 99,
  UPDATE_CHILDREN_PERMISSIONS = 100,
  LOCK = 101,
  UNLOCK = 102,
  ACCEPT_CONDITIONS = 103,
  SEND_NOTIFICATION_TEXT = 104,
  SEND_PRIVASPHERE_STANDARD = 105,
  SEND_PRIVASPHERE_REGISTERED = 106,
  SEND_PRIVASPHERE_READ = 107,
  VOTING_VOTE = 108,
  LOGIN_AZURE_AD_SSO = 109,
  EXPIRED = 110,
  SEND_NOTIFICATION = 111,
}

export interface HistoryLogAuthor {
  rid: number;
  displayName: string;
  shortName?: string;
  subtype?: number;
  imageDefined?: boolean;
  deleted?: boolean;
}

export interface HistoryLogEntry {
  logId: string;
  action: number;
  timestamp: number;
  hasDetails: boolean;
  author: Nullable<HistoryLogAuthor>;
  reference?: Nullable<HistoryLogAuthor>;
  keyId?: number;
}

export interface HistoryLogDetailItem {
  propertyName: string;
  oldVal?: unknown;
  newVal?: unknown;
}

export interface HistoryLogDetail extends HistoryLogEntry {
  details?: HistoryLogDetailItem[];
}

export enum Widget {
  APPROVAL_REQUEST = 1,
  SIGNATURE = 2,
  PENDING_TASK = 8,
  EVENT_ATTENDANCE = 32,
  UPCOMING_EVENT = 128,
  VOTING_REQUEST = 256,
}

export type HomeResourceWidget =
  | Widget.APPROVAL_REQUEST
  | Widget.SIGNATURE
  | Widget.PENDING_TASK
  | Widget.EVENT_ATTENDANCE
  | Widget.UPCOMING_EVENT
  | Widget.VOTING_REQUEST;

export interface HomeResource extends AbstractResourceItem {
  rid: number;
  displayName: string;
  authorId: number;
  authorName: string;
  authorImageDefined: boolean;
  createDate: number;
  updateDate: number;
  description: string;
  permission: number;
  type: number;
  subtype: number;
  updateStatus: number;
  deleted: boolean;
  sortType: number;
  breadcrumb: Breadcrumb[];
  widget: number;
}

export interface HomeRoom extends AbstractResourceItem {
  children: HomeResource[];
  pid: number;
}

export type EventChildren = AgendaPoint | Room | Voting | File;
export type RoomChildren = AnyResource;

export interface GroupEntity extends ResourceEntity {
  memberships: MembershipEntity[];
  roomId: number;
  ldapObjectGuid: string;
  ldapCn: string;
}

export interface UserEntity extends ResourceEntity {
  displayName: string;
  member: boolean;
  expiredOneTimeGuest: boolean;
  allowedToSign: boolean;
  password: string;
  authenticationMethod: number;
  lastLogin: number;
  username: string;
  firstName: string;
  address: string;
  zip: string;
  locality: string;
  country: string;
  company: string;
  department: string;
  position: string;
  phoneHome: string;
  phoneWork: string;
  mobile: string;
  fax: string;
  email: string;
  image: string;
  dateOfBirth: string;
  showDateOfBirthInCalendar: boolean;
  language: string;
  memberships: MembershipEntity[];
  roomId: number;
  imageSmall: string;
  guestCreation: boolean;
  wrongLoginCounter: number;
  lastPasswordChange: number;
  ldapObjectGuid: string;
  ldapCn: string;
  signature: number;
  conversationsDiskSpace: number;
  defaultView: number;
  otpSharedKey: string;
  geolocationFilter: string;
  geolocationSupport: GeolocationSupport;
  signatureImage: string;
  conditionsAccepted: number;
  signatureCountry: string;
  signatureSeal: boolean;
  systemToken: boolean;
}

export interface AgendaPointAttender extends SelectionItem {
  responsible?: boolean;
  speaker?: boolean;
  doNotAppear?: boolean;
  unauthorized?: boolean;
}

export interface AgendaPoint extends ContainerResource<ResourceType.AGENDA_POINT> {
  sort: number;
  duration: number;
  attenders?: AgendaPointAttender[];
  minutes: string;
  fromDate: number;
  toDate: number;
  index: number;
  reference: string;
  parentIndex: string;
  fullIndex: string;
  attendersPropagate: boolean;
}

export type SubType = {
  [ResourceType.COMMUNITY]: CommunitySubType;
  [ResourceType.USER]: UserSubType;
  [ResourceType.GROUP]: GroupSubType;
  [ResourceType.ROOM]: RoomSubType;
  [ResourceType.FILE]: FileSubType;
  [ResourceType.DISCUSSION]: number;
  [ResourceType.EVENT]: EventSubType | number;
  [ResourceType.VOTING]: VotingSubType;
  [ResourceType.REQUEST]: number;
  [ResourceType.AGENDA_POINT]: AgendaPointSubType | number;
  [ResourceType.ACTIVITY]: number;
  [ResourceType.CONVERSATION]: ConversationSubType;
};

export enum ResourceType {
  COMMUNITY = 1,
  USER = 2,
  GROUP = 3,
  ROOM = 4,
  FILE = 5,
  DISCUSSION = 6,
  EVENT = 7,
  VOTING = 8,
  REQUEST = 9,
  AGENDA_POINT = 10,
  ACTIVITY = 11,
  CONVERSATION = 12,
}

export interface SelectionItem<T extends ResourceType = ResourceType> {
  rid: number;
  displayName?: string;
  shortName?: string;
  imageDefined?: boolean;
  type: ResourceType | number;
  subtype?: SubType[T];
  deleted?: boolean;
  inUse?: boolean;
}

export enum PERMISSION_INHERITANCE {
  NONE = -1,
  DEFAULT = 0,
  FORCE = 1,
  FORCE_INHERITED = 2,
}

export enum PermissionLevel {
  ACCESS_DENIED = -1,
  SEC_VIEW = 1,
  SEC_PRINT = 2,
  ACCESS = 3,
  UPLOAD = 3.5,
  CONTRIBUTE = 4,
  MODIFY_AND_DELETE = 5,
}

export interface PermissionItem extends SelectionItem {
  inheritance: PERMISSION_INHERITANCE;
  level: PermissionLevel;
  inUse?: boolean;
}

export interface PermissionId {
  resource: ResourceEntity;
  owner: ResourceEntity;
}

export interface PermissionEntity {
  ownerExternalUser: boolean;
  ownerExternalGroup: boolean;
  permissionId: PermissionId;
  resource: ResourceEntity;
  owner: ResourceEntity;
  level: number;
  inheritance: number;
  mac: string;
}

export interface ResourcePermissions {
  name: string;
  items: PermissionItem[];
}

export interface AbstractResourceList {
  resources: Record<string, unknown>[];
}

export interface Approver extends SelectionItem {
  option: number;
  position?: number;
  placeholder?: string;
  status?: number;
  authenticationMethod?: number;
  language?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  mobile?: string;
  expireDate?: number;
  tempId?: number;
}

export interface Breadcrumb {
  shortName: string;
  subtype: number;
  rid: number;
  displayName: string;
  image: string;
  imageDefined: boolean;
  type: number;
  permission: number;
}

export enum PermissionsApplyOnChildren {
  NO,
  ALL,
  CHANGES,
}

export enum AgendaPointStatus {
  PROPOSED = 1,
}

export enum AgendaPointSubType {
  INFO = 2,
  DECISION = 3,
  BREAK = 4,
  SOUNDING = 5,
  WORKSHOP = 6,
  RECOMMENDATION = 7,
  DOCUMENTATION = 8,
}

export enum CommunityStatus {
  ACTIVE = 0,
  DISABLED = 2,
  SUSPENDED = 3,
  DELETED = 4,
}

export enum UserStatus {
  ACTIVE = 0,
  PASSWORD_KNOWN = 1,
  PASSWORD_UNKNOWN = 2,
  DISABLED = 3,
  BLOCKED = 6,
}

export enum FileStatus {
  PUBLISHED = 0,
  TO_BE_APPROVED = 1,
  REJECTED = 2,
  DELETED = 4,
}

export type ResourceStatus = {
  [ResourceType.COMMUNITY]: CommunityStatus;
  [ResourceType.USER]: UserStatus;
  [ResourceType.GROUP]: number;
  [ResourceType.ROOM]: number;
  [ResourceType.FILE]: FileStatus;
  [ResourceType.DISCUSSION]: number;
  [ResourceType.EVENT]: number;
  [ResourceType.VOTING]: VotingStatus;
  [ResourceType.REQUEST]: number;
  [ResourceType.AGENDA_POINT]: AgendaPointStatus;
  [ResourceType.ACTIVITY]: number;
  [ResourceType.CONVERSATION]: number;
};

export interface Resource<T extends ResourceType> {
  type: T;
  shortName: string;
  displayName: string;
  rid: number;
  name: string;
  pid: number;
  cid: number;
  subtype: SubType[T];
  description: string;
  createDate: number;
  updateDate: number;
  visibleFrom: number;
  visibleFromInFuture: boolean;
  expireDate: number;
  authorId: number;
  authorName: string;
  authorShortName: string;
  authorImageDefined: boolean;
  authorSubtype: number;
  authorDeleted: boolean;
  status: ResourceStatus[T];
  permissions: ResourcePermissions;
  notification: number;
  notificationPriorityHigh: boolean;
  notificationPublic: boolean;
  notificationSelection: SelectionItem[];
  notificationPropagate: boolean;
  approval: number;
  approvalSignature: SignatureType | 0;
  approvalNotification: number;
  approvalMustEverybodyVote: boolean;
  approvalDeadline: number;
  approvalDuration: number;
  approvalStartDate: number;
  approvers: Approver[];
  permission: number;
  breadcrumb: Breadcrumb[];
  path: string;
  permissionsApplyOnChildren: PermissionsApplyOnChildren;
  inheritSettings: boolean;
  bookmarked: boolean;
  deleted: boolean;
  activitiesCount: number;
  notSendNotification: boolean;
  prid: number;
  approvalAuthorId: number;
  approvalAuthorName: string;
  approvalMessage: string;
  approvalWithOrdering: boolean;
}

export type AnyResource = Resource<ResourceType>;

export type ResourceChildren = {
  [ResourceType.EVENT]: EventChildren[];
};

export const RESOURCE_COLOR_VALUES = {
  NONE: '',
  RED: 'red',
  DARK_RED: 'darkred',
  YELLOW: 'yellow',
  ORANGE: 'orange',
  GREEN: 'green',
  DARK_GREEN: 'darkgreen',
  WATER_GREEN: 'watergreen',
  BLUE: 'blue',
  DARK_BLUE: 'darkblue',
  VIOLET: 'violet',
  PURPLE: 'purple',
  BROWN: 'brown',
} as const;

export const RESOURCE_COLOR_HEX: Record<Exclude<ResourceColor, ''>, string> = {
  [RESOURCE_COLOR_VALUES.RED]: '#ed205b',
  [RESOURCE_COLOR_VALUES.ORANGE]: '#ee8517',
  [RESOURCE_COLOR_VALUES.YELLOW]: '#efbc20',
  [RESOURCE_COLOR_VALUES.GREEN]: '#59c936',
  [RESOURCE_COLOR_VALUES.BLUE]: '#1b9fe6',
  [RESOURCE_COLOR_VALUES.VIOLET]: '#bc68d0',
  [RESOURCE_COLOR_VALUES.BROWN]: '#927551',
  [RESOURCE_COLOR_VALUES.DARK_BLUE]: '#01307c',
  [RESOURCE_COLOR_VALUES.DARK_RED]: '#871c09',
  [RESOURCE_COLOR_VALUES.DARK_GREEN]: '#17660b',
  [RESOURCE_COLOR_VALUES.PURPLE]: '#5a2d87',
  [RESOURCE_COLOR_VALUES.WATER_GREEN]: '#33e8c0',
};

export type ResourceColor =
  (typeof RESOURCE_COLOR_VALUES)[keyof typeof RESOURCE_COLOR_VALUES];

export const ResourceColors: ResourceColor[] = Object.values(
  RESOURCE_COLOR_VALUES
);

export interface ContainerResource<T extends ResourceType> extends Resource<T> {
  sortType: number;
  maxVersions: number;
  maxLifetime: number;
  showInWelcome: boolean;
  imageDefined: boolean;
  imageSmall: string;
  category: ResourceColor;
  size: number;
  children?: AnyResource[];
}

export interface AbstractCommentableResource<
  T extends ResourceType,
> extends Resource<T> {
  commentsCount: number;
  lastCommentDate: number;
}

export interface ResourceApproverId {
  resource: ResourceEntity;
  approver: ResourceEntity;
}

export interface ResourceApproverEntity {
  id: ResourceApproverId;
  resource: ResourceEntity;
  approver: ResourceEntity;
  option: number;
  position: number;
  placeholder: string;
  status: number;
}

export interface NotificationSelectionId {
  resource: ResourceEntity;
  recipient: ResourceEntity;
}

export interface NotificationSelectionEntity {
  id: NotificationSelectionId;
  resource: ResourceEntity;
  recipient: ResourceEntity;
}

export interface ResourceEntity {
  maxGuestAccess: PermissionLevel;
  validChildTypes: ResourceType[];
  updated: boolean;
  visible: boolean;
  container: boolean;
  leaf: boolean;
  guestAccessSupported: boolean;
  rid: number;
  cid: number;
  pid: number;
  author: UserEntity;
  type: number;
  subtype: SubType;
  name: string;
  description: string;
  updateDate: number;
  createDate: number;
  visibleFrom: number;
  expireDate: number;
  status: number;
  notification: number;
  notificationPriorityHigh: boolean;
  notificationPublic: boolean;
  notificationRecipients: NotificationSelectionEntity[];
  approval: number;
  approvalNotification: number;
  approvalMustEverybodyVote: boolean;
  resourceApprovers: ResourceApproverEntity[];
  permissions: PermissionEntity[];
  permission: number;
  path: string;
  bookmarked: boolean;
  deleted: boolean;
  prid: number;
  approvalAuthorId: number;
  approvalDuration: number;
  approvalStartDate: number;
  approvalMessage: string;
  approvalWithOrdering: boolean;
}

export interface AbstractResourceItem {
  authorShortName: string;
  permission: number;
  displayName: string;
  rid: number;
  authorId: number;
  authorName: string;
  createDate: number;
  updateDate: number;
  description: string;
  type: number;
  subtype: number;
  updateStatus: number;
  authorImageDefined: boolean;
  deleted: boolean;
}

export enum CommunitySubType {
  PRODUCTIVE = 1,
  INTERN = 2,
  TRIAL = 3,
  PRE_PRODUCTIVE = 4,
}

export enum EventSubType {
  EVENT = 2,
  MEETING = 3,
}

export enum FileSubType {
  ROOT = 1,
  FILE = 2,
  FILE_SHORTCUT = 3,
  FILE_LINK = 4,
  FILE_NOTE = 5,
}

export enum GroupSubType {
  ROOT = 1,
  MEMBERS = 2,
  GUESTS = 3,
}

export enum RoomSubType {
  ROOMS_ROOT = 1,
  ROOM_USER = 2,
  ROOM_GROUP = 3,
  ROOM_GENERAL = 4,
  ROOM_CONTEXT = 5,
  ROOM_EVENT_FOLDER = 6,
  ROOM_SIGNING = 7,
}

export enum UserSubType {
  ROOT = 1,
  MEMBER = 2,
  GUEST = 3,
  ONE_TIME_GUEST = 4,
  AZURE_AD = 5,
}

export enum VotingSubType {
  BINARY = 2,
  CIRCULAR_RESOLUTION = 3,
  OPTION_LIST = 4,
}

export enum ConversationSubType {
  ROOT = 1,
  MESSAGE = 2,
  REFERENCE = 3,
  ATTACHMENT = 4,
}

export enum GeolocationSupport {
  NONE = 0,
  MOBILE_ID = 1,
  GEO_IP = 2,
  IP_FILTER = 3,
}

export enum DuplicateResolutionOption {
  CANCEL = 0,
  KEEP_BOTH = 1,
  NEW_VERSION = 2,
}

export interface FileVersionAttachment {
  attachmentId: number;
  name: string;
  preview: boolean;
}

export interface SignatureValidation {
  signedObjectIdentifier: string;
  signingTime: string;
  signingDate: number;
  issuerName: string;
  issuerCN: string;
  result: string;
  resultValue: number;
  valid: boolean;
  swisscom: boolean;
  advanced: boolean;
  qualified: boolean;
  eidas: boolean;
  seal: boolean;
  timestamp: boolean;
  signatureType: string;
}

export interface File extends AbstractCommentableResource<ResourceType.FILE> {
  permissionInheritance?: PERMISSION_INHERITANCE;
  compressedFormat: boolean;
  contentType: string;
  pdfA: boolean;
  pdfALevel: string;
  format: string;
  maxVersions: number;
  maxVersionsRoot: number;
  showInWelcome: boolean;
  version: number;
  filePath: number;
  size: number;
  address: string;
  versions: Version[];
  versionDescription: string;
  versionStatus: number;
  versionAuthorId: number;
  versionCreateDate: number;
  secView: boolean;
  secViewPending: boolean;
  secViewError: number;
  approver: boolean;
  approverActive: boolean;
  monitor: boolean;
  versionAuthorName: string;
  versionsCount: number;
  signedFile: boolean;
  signedFileSize: number;
  signedFileValidations: SignatureValidation[];
  versionPublicationDate: number;
  versionApprovalRequestDate: number;
  annotations: boolean;
  signedFileValid: boolean;
  signedFileValidityLevel: number;
  someoneCanSign: boolean;
  lockedById: number;
  lockedByName: string;
  lockedByShortName: string;
  lockedByImageDefined: boolean;
  lockedBySubtype: number;
  doNotSendNotification: boolean;
  lifetime: number;
  annotationList?: AnnotationAuthor[];
  compareVersion?: number;
  watermark?: string;
  checksum?: {
    algorithm: string;
    value: string;
  };
  attachments: FileVersionAttachment[];
  antiVirusLimitExceeded?: boolean;
  comment?: string;
}

export enum SignedFileValidityLevel {
  NO_SIGNED_FILE = 0,
  ALL_SIGNATURES_VALID = 1,
  AT_LEAST_ONE_SIGNATURE_COULD_NOT_BE_VALIDATED = 2,
  SIGNATURES_COULD_NOT_BE_VALIDATED = 3,
  AT_LEAST_ONE_SIGNATURE_IS_INVALID = 4,
  INVALID_SIGNATURES = 5,
}

export interface AnnotationAuthor {
  authorId: number;
  authorDisplayName: string;
  public: boolean;
  you: boolean;
}

export interface Version {
  rid: number;
  authorId: number;
  authorName: string;
  createDate: number;
  publicationDate: number;
  approvalRequestDate: number;
  status: number;
  compressedFormat: boolean;
  size: number;
  versionDescription: string;
  version: number;
  contentType: string;
  pdfA: boolean;
  pdfALevel: string;
  format: string;
  secView: boolean;
  secViewPending: boolean;
  secViewError: number;
  annotations: boolean;
  signedFile: boolean;
  signedFileSize: number;
  signedFileValidations: SignatureValidation[];
  signedFileValid: boolean;
  signedFileValidityLevel: SignedFileValidityLevel;
  approvalSignature: SignatureType | 0;
}

export interface MembershipId {
  user: UserEntity;
  group: GroupEntity;
}

export interface MembershipEntity {
  approved: boolean;
  membershipId: MembershipId;
  member: UserEntity;
  group: GroupEntity;
  changeAuthorId: number;
  updateDate: number;
  approvalDate: number;
  status: number;
  permission: number;
}

export interface Room extends ContainerResource<ResourceType.ROOM> {
  permissionInheritance?: PERMISSION_INHERITANCE;
  roomType: number;
  filesCount: number;
  discussionsCount: number;
  eventsCount: number;
  futureEventsCount: number;
  roomsCount: number;
  votingsCount: number;
  tasksCount: number;
}

export const WIN_OPTION_ABSTAIN = 0;
export const WIN_OPTION_YES = 1;
export const WIN_OPTION_NO = 2;
export const WIN_OPTION_ASK_FOR_DISCUSSION = 3;

export const WinOptionsMap = {
  ABSTAIN: WIN_OPTION_ABSTAIN,
  YES: WIN_OPTION_YES,
  NO: WIN_OPTION_NO,
  ASK_FOR_DISCUSSION: WIN_OPTION_ASK_FOR_DISCUSSION,
} as const;

export enum WinOptionBinary {
  YES = WIN_OPTION_YES,
  NO = WIN_OPTION_NO,
}

export enum WinOptionCircular {
  ABSTAIN = WIN_OPTION_ABSTAIN,
  YES = WIN_OPTION_YES,
  NO = WIN_OPTION_NO,
  ASK_FOR_DISCUSSION = WIN_OPTION_ASK_FOR_DISCUSSION,
}

export type WinOptionList = number;

export interface VotingOption {
  optionId: number;
  optionText: string;
  decisionText: string;
}

export type UserType =
  ResourceType.COMMUNITY | ResourceType.USER | ResourceType.GROUP;

export enum Attendance {
  PENDING = 0,
  PRESENT = 1,
  ABSENT = 2,
}

export interface AbstractInvitee extends SelectionItem<UserType> {
  attendance: Attendance;
  delegateId: number;
  delegateDisplayName: string;
  delegateShortName: string;
  delegateType: ResourceType;
  delegateSubtype: number;
  delegateImageDefined: boolean;
}

export interface VotingVoter extends AbstractInvitee {
  hasVoted: boolean;
  castingVote: boolean;
}

type WinOption<T> = T extends VotingSubType.BINARY
  ? WinOptionBinary
  : T extends VotingSubType.CIRCULAR_RESOLUTION
    ? WinOptionCircular
    : T extends VotingSubType.OPTION_LIST
      ? WinOptionList
      : never;

export enum VotingStatus {
  NEW = 0,
  RUNNING = 1,
  STOPPED = 2,
  PASSED = 3,
  NOT_PASSED = 4,
  CORRUPTED = 5,
}

export interface Voting<
  T extends VotingSubType = VotingSubType,
> extends Resource<ResourceType.VOTING> {
  options: VotingOption[];
  majority: number;
  majorityThreshold: number;
  secret: boolean;
  startDate: number;
  stopDate: number;
  voters: VotingVoter[];
  sort: number;
  masterVoting: number;
  winOption: WinOption<T>;
  winOptionText: string;
  signature: boolean;
  revote: boolean;
  deadline: number;
  displayVotes: number;
  language: string;
  templateId: number;
  internalUpdate: boolean;
  status: VotingStatus;
}

export interface PermissionScheme {
  name: string;
  items: PermissionItem[];
}

export interface PermissionLevelItem {
  value: number;
  label?: string;
}

export interface ResourceCreationConfigPermissions {
  selection: {
    items: SelectionItem[];
  };
  schemes?: PermissionScheme[];
  levels?: PermissionLevelItem[];
}

export interface ResourceCreationConfigNotification {
  notification: number;
  notificationPriorityHigh: boolean;
  notificationPublic: boolean;
  notificationSelection?: SelectionItem[];
  notificationPropagate?: boolean;
}

export interface ResourceCreationConfigApproval {
  approval: number;
  approvalNotification: number;
  approvalMustEverybodyVote: boolean;
  approvalWithSignature: boolean;
  approvers: SelectionItem[];
}

export interface ResourceCreationConfig {
  maxVersions: number;
  sortType: number;
  category: ResourceColor;
  permissions: ResourceCreationConfigPermissions;
  notification?: ResourceCreationConfigNotification;
  approval?: ResourceCreationConfigApproval;
  maxLifetime?: number;
  freeSpace?: number;
  maxFileSize?: number;
}

export interface FinderDestination {
  rid: number;
  type: ResourceType;
  parentSubtype: number;
  permission: number;
  level?: number;
  breadcrumb?: Breadcrumb[];
}

export type ResourcePath = ResourceType | 'TEMPLATE' | 'NOTIFICATION';

export interface RootState {
  rootGroupsId: number;
  rootGroupsPermission: number;
  rootRoomsId: number;
  rootRoomsPermission: number;
  rootUsersId: number;
  rootUsersPermission: number;
  signingRoomId: number;
  signingRoomPermission: number;
}

export interface CommunityState {
  communityId: number;
  communityPermission: PermissionLevel;
  communityExpireDate: number;
  communityName: string;
  communityProfileId?: number;
  communityReporting: boolean;
  communitySignature: boolean;
  communityStatus: CommunityStatus;
  communitySubtype: CommunitySubType;
  communityTimeout: number;
}

export interface SystemSettings {
  DEFAULT_LANGUAGE?: string;
  SAML_BASE_URL?: string;
  APP_BASE_URL?: string;
  LANGUAGES?: string[];
}
